import React, { useMemo, useState, useRef, useEffect } from "react";
import {
  X,
  Plus,
  Trash2,
  CheckCircle2,
  ShieldAlert,
  ShieldCheck,
  ShieldQuestion,
  Syringe,
  Bug,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import "../styles/vetAssistantChat.css";
import { authFetch } from "../utils/authFetch";
import drpawsImg from "../assets/drpaws.webp";

// =========================================================
// 🚦 ¿SE PUEDE SALTAR EL DIAGNÓSTICO?
//
// Durante el registro (crear cuenta → mascota → Dra. Vetia) NO: el
// diagnóstico es parte del onboarding y la persona debe hacerlo, así que
// el botón "Hacerlo después" no aparece.
//
// Abierto desde la interfaz o desde el perfil de una mascota SÍ: ahí ya
// es una consulta más y tiene sentido poder dejarla para luego.
//
// Se decide por la ruta para no tener que tocar los componentes que
// abren el chat. Si algún día hace falta forzarlo a mano, basta con
// pasar la prop allowSkip: <VetAssistantChat allowSkip={false} ... />
// =========================================================
const ONBOARDING_PATHS = ["/welcomepet", "/perfilmascota", "/anadirmascota"];

function getHour() {
  return new Date().toLocaleTimeString("es-CO", {
    hour: "numeric",
    minute: "2-digit",
  });
}

function readUserIdFromStorage() {
  const rawId = localStorage.getItem("idUser");
  if (rawId && !Number.isNaN(Number(rawId))) return Number(rawId);

  try {
    const rawUser = localStorage.getItem("petfamUser");
    if (!rawUser) return null;
    const u = JSON.parse(rawUser);
    const id = u?.idUser ?? u?.id ?? u?.userId ?? null;
    if (id && !Number.isNaN(Number(id))) return Number(id);
  } catch {}
  return null;
}

const makeRowId = () => `ROW-${Date.now()}-${Math.random().toString(16).slice(2)}`;

// ✅ Fecha de hoy en formato YYYY-MM-DD (local, no UTC) — tope para inputs
// de fecha de eventos que ya ocurrieron (vacunas/desparasitaciones anotadas).
function getTodayISO() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function isFutureDate(dateStr) {
  if (!dateStr) return false;
  return String(dateStr).slice(0, 10) > getTodayISO();
}

// 📅 Muestra una fecha YYYY-MM-DD como DD/MM/YYYY.
// Se hace con texto (sin new Date) para evitar que la zona horaria
// corra la fecha un día. Solo es para mostrar: el backend sigue
// recibiendo y enviando YYYY-MM-DD.
function formatDateDMY(dateStr) {
  if (!dateStr) return "";
  const match = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return String(dateStr);
  const [, yyyy, mm, dd] = match;
  return `${dd}/${mm}/${yyyy}`;
}

function mapProtectionStatus(status) {
  const s = String(status || "").toUpperCase();

  if (s === "SIN_PROTECCION") {
    return {
      label: "🔴 Sin protección",
      icon: <ShieldAlert size={18} />,
      className: "status-card red",
    };
  }

  if (s === "PROTEGIDO") {
    return {
      label: "🟢 Protegido",
      icon: <ShieldCheck size={18} />,
      className: "status-card green",
    };
  }

  return {
    label: "🟡 En proceso",
    icon: <ShieldQuestion size={18} />,
    className: "status-card yellow",
  };
}

function formatRecommendedAction(action) {
  const a = String(action || "").toUpperCase();
  switch (a) {
    case "INICIAR":
      return "Iniciar esquema";
    case "CONTINUAR":
      return "Continuar esquema";
    case "CONTINUAR_URGENTE":
      return "Continuar cuanto antes";
    case "REINICIAR":
      return "Reiniciar esquema";
    case "MANTENER_ANUAL":
      return "Mantener refuerzo anual";
    case "REFUERZO_HOY":
      return "Aplicar refuerzo hoy";
    default:
      return "Seguir plan recomendado";
  }
}

function formatEventType(type) {
  const t = String(type || "").toUpperCase();
  if (t === "DEWORMING_INTERNAL") return "Desparasitación interna";
  if (t === "DEWORMING_EXTERNAL") return "Desparasitación externa";
  return "Vacuna";
}

function typeIcon(type) {
  const t = String(type || "").toUpperCase();
  if (t === "DEWORMING_INTERNAL" || t === "DEWORMING_EXTERNAL") return <Bug size={16} />;
  return <Syringe size={16} />;
}

function upper(v) {
  return String(v || "").toUpperCase();
}

export default function VetAssistantChat({ pet, onClose, onVaccinesSaved, allowSkip }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saveMsg, setSaveMsg] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [seenAt, setSeenAt] = useState("");

  // 🚦 Si quien abre el chat pasa allowSkip, manda esa prop. Si no, se
  // deduce por la ruta: en el onboarding NO se puede saltar.
  const canSkip = useMemo(() => {
    if (typeof allowSkip === "boolean") return allowSkip;

    const path = String(location?.pathname || "").toLowerCase();
    return !ONBOARDING_PATHS.some((p) => path.startsWith(p));
  }, [allowSkip, location?.pathname]);

  const [answers, setAnswers] = useState({
    vaccineStatus: null,
    dewormingStatus: null,
  });

  const [vaccinePlan, setVaccinePlan] = useState([]);
  const [dewormingPlan, setDewormingPlan] = useState([]);

  // Vacunas estructuradas
  const [structuredRows, setStructuredRows] = useState([
    { id: makeRowId(), vaccineType: "", dose: "", date: "", veterinarian: "" },
  ]);

  // Vacunas extra
  const [showCustomVaccines, setShowCustomVaccines] = useState(false);
  const [customRows, setCustomRows] = useState([
    { id: makeRowId(), name: "", date: "", veterinarian: "" },
  ]);

  // Desparasitación anotada
  const [dewormingForm, setDewormingForm] = useState({
    internalDate: "",
    externalDate: "",
  });

  const petName = useMemo(() => pet?.name || pet?.nombre || "tu mascota", [pet]);

  const resolvedUserId = useMemo(() => {
    const fromPet = pet?.idUser ?? pet?.ownerId ?? null;
    if (fromPet != null && !Number.isNaN(Number(fromPet))) return Number(fromPet);
    return readUserIdFromStorage();
  }, [pet]);

  const resolvedPetId = useMemo(() => {
    const id = pet?.idPet ?? pet?.id ?? pet?.id_pet ?? null;
    return id != null && !Number.isNaN(Number(id)) ? Number(id) : null;
  }, [pet]);

  const speciesNorm = useMemo(() => {
    const s = String(pet?.species || "").toLowerCase();
    return s.includes("gat") ? "GATO" : "PERRO";
  }, [pet]);

  const typingTimerRef = useRef(null);
  const simulateTyping = (nextStep, delay = 850) => {
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    setIsTyping(true);
    typingTimerRef.current = setTimeout(() => {
      setIsTyping(false);
      setStep(nextStep);
    }, delay);
  };

  useEffect(() => {
    return () => {
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    };
  }, []);

  const markSeenNow = () => setSeenAt(getHour());

  const ensureIdsOrFail = (kind = "operación") => {
    if (!resolvedUserId || !resolvedPetId) {
      setError(
        `No pude identificar el usuario o la mascota para ${kind}. Cierra el chat y ábrelo de nuevo desde la mascota correcta.`
      );
      return false;
    }
    return true;
  };

  const handleSelect = (field, value) => {
    setAnswers((prev) => ({ ...prev, [field]: value }));
  };

  const addStructuredRow = () => {
    setStructuredRows((prev) => [
      ...prev,
      { id: makeRowId(), vaccineType: "", dose: "", date: "", veterinarian: "" },
    ]);
  };

  const updateStructuredRow = (id, field, value) => {
    setStructuredRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: field === "veterinarian" ? upper(value) : value } : r))
    );
  };

  const removeStructuredRow = (id) => {
    setStructuredRows((prev) => (prev.length > 1 ? prev.filter((r) => r.id !== id) : prev));
  };

  const addCustomRow = () => {
    setCustomRows((prev) => [...prev, { id: makeRowId(), name: "", date: "", veterinarian: "" }]);
  };

  const updateCustomRow = (id, field, value) => {
    setCustomRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: field === "veterinarian" ? upper(value) : value } : r))
    );
  };

  const removeCustomRow = (id) => {
    setCustomRows((prev) => (prev.length > 1 ? prev.filter((r) => r.id !== id) : prev));
  };

  const buildStructuredPayload = () => {
    return structuredRows
      .map((r) => ({
        vaccineType: r.vaccineType,
        dose: r.dose,
        date: r.date || null,
        veterinarian: (r.veterinarian || "").trim(),
      }))
      .filter((r) => r.vaccineType && r.dose && r.date);
  };

  const buildCustomPayload = () => {
    return customRows
      .map((r) => ({
        name: (r.name || "").trim(),
        date: r.date || null,
        veterinarian: (r.veterinarian || "").trim(),
      }))
      .filter((r) => r.name && r.date);
  };

  const handleGenerateVaccinePlan = async () => {
    if (!ensureIdsOrFail("crear el plan de vacunación")) return;

    if (answers.vaccineStatus === "ANOTADAS") {
      const anyFutureDate = [...structuredRows, ...customRows].some((r) => isFutureDate(r.date));
      if (anyFutureDate) {
        setError("La fecha de una vacuna no puede ser futura. Corrígela antes de continuar.");
        return;
      }
    }

    try {
      setLoading(true);
      setError("");
      setSaveMsg("");
      setVaccinePlan([]);

      const payload = {
        petId: resolvedPetId,
        idUser: resolvedUserId,
        species: pet?.species,
        birthDate: pet?.birthDate,
        vaccineStatus: answers.vaccineStatus,
        structuredVaccines: answers.vaccineStatus === "ANOTADAS" ? buildStructuredPayload() : [],
        customVaccines: answers.vaccineStatus === "ANOTADAS" ? buildCustomPayload() : [],
      };

      if (answers.vaccineStatus === "ANOTADAS" && payload.structuredVaccines.length === 0) {
        setError("Debes añadir al menos una vacuna estructurada para analizar el estado de protección.");
        return;
      }

      const res = await authFetch(`/vet-assistant/vaccines/plan/save`, {
        method: "POST",
        body: JSON.stringify(payload),
      });

      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.data) {
        throw new Error(json?.userMessage || "No se pudo generar el plan de vacunas.");
      }

      setVaccinePlan(Array.isArray(json.data) ? json.data : []);
      if (typeof onVaccinesSaved === "function") onVaccinesSaved();

      simulateTyping(99, 650);
    } catch (e) {
      console.error(e);
      setError("Hubo un problema generando el plan de vacunas.");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateDewormingPlan = async () => {
    if (!ensureIdsOrFail("crear el plan de desparasitaciones")) return;

    if (
      answers.dewormingStatus === "ANOTADAS" &&
      (isFutureDate(dewormingForm.internalDate) || isFutureDate(dewormingForm.externalDate))
    ) {
      setError("La fecha de desparasitación no puede ser futura. Corrígela antes de continuar.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      setSaveMsg("");
      setDewormingPlan([]);

      const payload = {
        petId: resolvedPetId,
        idUser: resolvedUserId,
        birthDate: pet?.birthDate,
        dewormingStatus: answers.dewormingStatus,
        internalDate: answers.dewormingStatus === "ANOTADAS" ? dewormingForm.internalDate || null : null,
        externalDate: answers.dewormingStatus === "ANOTADAS" ? dewormingForm.externalDate || null : null,
      };

      if (
        answers.dewormingStatus === "ANOTADAS" &&
        !payload.internalDate &&
        !payload.externalDate
      ) {
        setError("Debes ingresar al menos una fecha de desparasitación.");
        return;
      }

      const res = await authFetch(`/vet-assistant/deworming/plan/save`, {
        method: "POST",
        body: JSON.stringify(payload),
      });

      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.data) {
        throw new Error(json?.userMessage || "No se pudo generar el plan de desparasitaciones.");
      }

      setDewormingPlan(Array.isArray(json.data) ? json.data : []);
      if (typeof onVaccinesSaved === "function") onVaccinesSaved();

      simulateTyping(199, 650);
    } catch (e) {
      console.error(e);
      setError("Hubo un problema generando el plan de desparasitaciones.");
    } finally {
      setLoading(false);
    }
  };

  const firstVaccineMeta = vaccinePlan?.[0] || null;
  const protectionUi = mapProtectionStatus(firstVaccineMeta?.protectionStatus);

  const firstDewormingMeta = dewormingPlan?.[0] || null;

  const VetBubble = ({ children, variant = "" }) => (
    <div className="chat-row vet">
      <div className={`vet-msg ${variant}`}>{children}</div>
    </div>
  );

  const UserActions = ({ children }) => (
    <div className="chat-row user">
      <div className="user-options">
        {children}
        <span className="msg-seen">{seenAt ? `Visto • ${seenAt}` : "Visto"}</span>
      </div>
    </div>
  );

  const renderVaccineResult = () => (
    <>
      <VetBubble>
        <p>Perfecto 💛 voy a revisar el estado real de protección de tu mascota y te diré qué sigue.</p>
        <span className="msg-hour">{getHour()}</span>
      </VetBubble>

      <VetBubble>
        <div className={protectionUi.className}>
          <div className="status-card__head">
            {protectionUi.icon}
            <strong>{protectionUi.label}</strong>
          </div>

          {!!firstVaccineMeta?.recommendedAction && (
            <p><strong>Acción recomendada:</strong> {formatRecommendedAction(firstVaccineMeta.recommendedAction)}</p>
          )}

          {!!firstVaccineMeta?.nextStepMessage && (
            <p><strong>Próximo paso:</strong> {firstVaccineMeta.nextStepMessage}</p>
          )}
        </div>
        <span className="msg-hour">{getHour()}</span>
      </VetBubble>

      {!!firstVaccineMeta?.vetiaMessage && (
        <VetBubble>
          <p>{firstVaccineMeta.vetiaMessage}</p>
          <span className="msg-hour">{getHour()}</span>
        </VetBubble>
      )}

      <VetBubble>
        <p className="vet-section-title">Plan de vacunación:</p>
        <div className="plan-events-list">
          {vaccinePlan.map((v, idx) => (
            <div key={`${v.title}-${v.date}-${idx}`} className="plan-event-card">
              <div className="plan-event-card__type">
                {typeIcon(v.type)}
                <span>{formatEventType(v.type)}</span>
              </div>
              <p className="plan-event-card__title">{v.title}</p>
              <p className="plan-event-card__date">📅 {formatDateDMY(v.date)}</p>
              {!!v.description && <p className="plan-event-card__desc">{v.description}</p>}
            </div>
          ))}
        </div>
        <span className="msg-hour">{getHour()}</span>
      </VetBubble>

      <UserActions>
        <button onClick={() => { markSeenNow(); simulateTyping(120); }}>
          Continuar con desparasitaciones
        </button>
      </UserActions>
    </>
  );

  const renderContent = () => {
    if (step === 0) {
      return (
        <>
          <VetBubble>
            <p>
              Hola, soy tu asistente veterinaria digital en Petfam. Estoy aquí para ayudarte a
              organizar las vacunas y desparasitaciones de tu mascota y recordártelas a tiempo.
            </p>
            <span className="msg-hour">{getHour()}</span>
          </VetBubble>

          <UserActions>
            <button onClick={() => { markSeenNow(); simulateTyping(10); }}>
              Organizar ahora
            </button>

            {/* 🚦 "Hacerlo después" NO aparece durante el registro: ahí el
                diagnóstico es parte del onboarding. Sí aparece cuando el
                chat se abre desde la interfaz. */}
            {canSkip && (
              <button className="secondary" onClick={() => { markSeenNow(); onClose?.(); }}>
                Hacerlo después
              </button>
            )}
          </UserActions>
        </>
      );
    }

    if (step === 10) {
      return (
        <>
          <VetBubble>
            <p>
              Vamos a iniciar con el plan de vacunación:
              <br />
              <strong>¿Sabes si ya le han puesto vacunas a tu mascota?</strong>
            </p>
            <span className="msg-hour">{getHour()}</span>
          </VetBubble>

          <UserActions>
            <button onClick={() => { markSeenNow(); handleSelect("vaccineStatus", "NINGUNA"); simulateTyping(20); }}>
              No, nunca
            </button>
            <button onClick={() => { markSeenNow(); handleSelect("vaccineStatus", "NO_RECUERDO"); simulateTyping(20); }}>
              Sí, pero no recuerdo cuáles
            </button>
            <button onClick={() => { markSeenNow(); handleSelect("vaccineStatus", "ANOTADAS"); simulateTyping(30); }}>
              Sí, las tengo anotadas (carnet)
            </button>
          </UserActions>
        </>
      );
    }

    if (step === 20) {
      return (
        <>
          <VetBubble>
            <p>Perfecto 💛 voy a revisar el estado real de protección de tu mascota y te diré qué sigue.</p>
            <span className="msg-hour">{getHour()}</span>
          </VetBubble>

          {loading && (
            <VetBubble>
              <p>Analizando…</p>
              <span className="msg-hour">{getHour()}</span>
            </VetBubble>
          )}

          {error && (
            <VetBubble variant="vet-msg--error">
              <p>{error}</p>
              <span className="msg-hour">{getHour()}</span>
            </VetBubble>
          )}

          <UserActions>
            <button onClick={() => { markSeenNow(); handleGenerateVaccinePlan(); }} disabled={loading}>
              Generar plan de vacunación
            </button>
          </UserActions>
        </>
      );
    }

    if (step === 30) {
      return (
        <>
          <VetBubble>
            <p>
              Perfecto, añade las vacunas a continuación para que pueda analizar el estado de protección de tu mascota.
            </p>
            <span className="msg-hour">{getHour()}</span>
          </VetBubble>

          {error && (
            <VetBubble variant="vet-msg--error">
              <p>{error}</p>
              <span className="msg-hour">{getHour()}</span>
            </VetBubble>
          )}

          <VetBubble>
            <p className="vet-section-title">Vacunas estructuradas</p>

            <div className="vet-items">
              {structuredRows.map((row) => (
                <div key={row.id} className="vet-structured-row">
                  <select
                    value={row.vaccineType}
                    onChange={(e) => updateStructuredRow(row.id, "vaccineType", e.target.value)}
                  >
                    <option value="">Tipo de vacuna</option>
                    {speciesNorm === "PERRO" ? (
                      <>
                        <option value="POLIVALENTE_CANINA">Polivalente canina</option>
                        <option value="RABIA">Rabia</option>
                      </>
                    ) : (
                      <>
                        <option value="TRIPLE_FELINA">Triple felina</option>
                        <option value="RABIA">Rabia</option>
                      </>
                    )}
                  </select>

                  <select
                    value={row.dose}
                    onChange={(e) => updateStructuredRow(row.id, "dose", e.target.value)}
                  >
                    <option value="">Dosis</option>
                    <option value="FIRST">1ª dosis</option>
                    <option value="SECOND">2ª dosis</option>
                    <option value="THIRD">3ª dosis</option>
                    <option value="ANNUAL">Refuerzo anual</option>
                  </select>

                  <input
                    type="date"
                    value={row.date}
                    max={getTodayISO()}
                    onChange={(e) => updateStructuredRow(row.id, "date", e.target.value)}
                  />

                  <input
                    value={row.veterinarian}
                    onChange={(e) => updateStructuredRow(row.id, "veterinarian", e.target.value)}
                    placeholder="Veterinario (opcional)"
                  />

                  <button
                    type="button"
                    className="vet-icon-btn"
                    onClick={() => removeStructuredRow(row.id)}
                    disabled={structuredRows.length === 1}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
            </div>

            <button type="button" onClick={addStructuredRow} className="vet-add-btn">
              <Plus size={16} /> Añadir otra vacuna estructurada
            </button>

            <button
              type="button"
              onClick={() => setShowCustomVaccines((s) => !s)}
              className="vet-link-btn"
            >
              Añadir otra vacuna (opcional)
            </button>

            {showCustomVaccines && (
              <div className="custom-vaccines-wrap">
                <p className="vet-section-title">Vacunas adicionales solo para carnet</p>

                {customRows.map((row) => (
                  <div key={row.id} className="vet-structured-row">
                    <input
                      value={row.name}
                      onChange={(e) => updateCustomRow(row.id, "name", e.target.value)}
                      placeholder="Nombre de la vacuna"
                    />

                    <div />

                    <input
                      type="date"
                      value={row.date}
                      max={getTodayISO()}
                      onChange={(e) => updateCustomRow(row.id, "date", e.target.value)}
                    />

                    <input
                      value={row.veterinarian}
                      onChange={(e) => updateCustomRow(row.id, "veterinarian", e.target.value)}
                      placeholder="Veterinario (opcional)"
                    />

                    <button
                      type="button"
                      className="vet-icon-btn"
                      onClick={() => removeCustomRow(row.id)}
                      disabled={customRows.length === 1}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                ))}

                <button type="button" onClick={addCustomRow} className="vet-add-btn">
                  <Plus size={16} /> Añadir otra vacuna adicional
                </button>
              </div>
            )}

            <span className="msg-hour">{getHour()}</span>
          </VetBubble>

          {loading && (
            <VetBubble>
              <p>Analizando…</p>
              <span className="msg-hour">{getHour()}</span>
            </VetBubble>
          )}

          <UserActions>
            <button onClick={() => { markSeenNow(); handleGenerateVaccinePlan(); }} disabled={loading}>
              Analizar estado y generar plan
            </button>

            <button className="secondary" onClick={() => { markSeenNow(); setStep(10); setError(""); }}>
              Volver
            </button>
          </UserActions>
        </>
      );
    }

    if (step === 99) {
      return renderVaccineResult();
    }

    if (step === 120) {
      return (
        <>
          <VetBubble>
            <p>
              Vamos a iniciar con el plan de desparasitaciones:
              <br />
              <strong>¿Sabes si ya ha sido desparasitado tu mascota?</strong>
            </p>
            <span className="msg-hour">{getHour()}</span>
          </VetBubble>

          <UserActions>
            <button onClick={() => { markSeenNow(); handleSelect("dewormingStatus", "NINGUNA"); simulateTyping(130); }}>
              No, nunca
            </button>
            <button onClick={() => { markSeenNow(); handleSelect("dewormingStatus", "NO_RECUERDO"); simulateTyping(130); }}>
              Sí, pero no recuerdo hace cuánto
            </button>
            <button onClick={() => { markSeenNow(); handleSelect("dewormingStatus", "ANOTADAS"); simulateTyping(140); }}>
              Sí, lo tengo anotado
            </button>
          </UserActions>
        </>
      );
    }

    if (step === 130) {
      return (
        <>
          <VetBubble>
            <p>Perfecto 💛 voy a calcular las próximas desparasitaciones interna y externa.</p>
            <span className="msg-hour">{getHour()}</span>
          </VetBubble>

          {loading && (
            <VetBubble>
              <p>Analizando…</p>
              <span className="msg-hour">{getHour()}</span>
            </VetBubble>
          )}

          {error && (
            <VetBubble variant="vet-msg--error">
              <p>{error}</p>
              <span className="msg-hour">{getHour()}</span>
            </VetBubble>
          )}

          <UserActions>
            <button onClick={() => { markSeenNow(); handleGenerateDewormingPlan(); }} disabled={loading}>
              Generar plan de desparasitaciones
            </button>
          </UserActions>
        </>
      );
    }

    if (step === 140) {
      return (
        <>
          <VetBubble>
            <p>
              Añade las fechas anotadas para calcular las próximas desparasitaciones.
            </p>
            <span className="msg-hour">{getHour()}</span>
          </VetBubble>

          {error && (
            <VetBubble variant="vet-msg--error">
              <p>{error}</p>
              <span className="msg-hour">{getHour()}</span>
            </VetBubble>
          )}

          <VetBubble>
            <div className="deworming-form">
              <label>
                <span>Desparasitación interna: fecha</span>
                <input
                  type="date"
                  value={dewormingForm.internalDate}
                  max={getTodayISO()}
                  onChange={(e) => setDewormingForm((p) => ({ ...p, internalDate: e.target.value }))}
                />
              </label>

              <label>
                <span>Desparasitación externa: fecha</span>
                <input
                  type="date"
                  value={dewormingForm.externalDate}
                  max={getTodayISO()}
                  onChange={(e) => setDewormingForm((p) => ({ ...p, externalDate: e.target.value }))}
                />
              </label>
            </div>
            <span className="msg-hour">{getHour()}</span>
          </VetBubble>

          {loading && (
            <VetBubble>
              <p>Analizando…</p>
              <span className="msg-hour">{getHour()}</span>
            </VetBubble>
          )}

          <UserActions>
            <button onClick={() => { markSeenNow(); handleGenerateDewormingPlan(); }} disabled={loading}>
              Guardar y calcular próximas desparasitaciones
            </button>

            <button className="secondary" onClick={() => { markSeenNow(); setStep(120); setError(""); }}>
              Volver
            </button>
          </UserActions>
        </>
      );
    }

    if (step === 199) {
      return (
        <>
          <VetBubble>
            <p>{firstDewormingMeta?.vetiaMessage || "Ya organicé las desparasitaciones interna y externa."}</p>
            <span className="msg-hour">{getHour()}</span>
          </VetBubble>

          {!!firstDewormingMeta?.nextStepMessage && (
            <VetBubble>
              <p><strong>Próximo paso:</strong> {firstDewormingMeta.nextStepMessage}</p>
              <span className="msg-hour">{getHour()}</span>
            </VetBubble>
          )}

          <VetBubble>
            <p className="vet-section-title">Plan de desparasitaciones:</p>
            <div className="plan-events-list">
              {dewormingPlan.map((v, idx) => (
                <div key={`${v.title}-${v.date}-${idx}`} className="plan-event-card">
                  <div className="plan-event-card__type">
                    {typeIcon(v.type)}
                    <span>{formatEventType(v.type)}</span>
                  </div>
                  <p className="plan-event-card__title">{v.title}</p>
                  <p className="plan-event-card__date">📅 {formatDateDMY(v.date)}</p>
                  {!!v.description && <p className="plan-event-card__desc">{v.description}</p>}
                </div>
              ))}
            </div>
            <span className="msg-hour">{getHour()}</span>
          </VetBubble>

          <UserActions>
            <button onClick={() => { markSeenNow(); onClose?.(); }}>
              Cerrar
            </button>
          </UserActions>
        </>
      );
    }

    return null;
  };

  return (
    <div className="vet-chat-overlay">
      <div className="vet-chat-panel">
        <div className="vet-chat-header">
          <div className="vet-chat-avatar">
            <img src={drpawsImg} alt="Dra.Vetia" loading="lazy" />
          </div>

          <div className="vet-chat-headtxt">
            <p className="vet-chat-title">Dra.Vetia</p>
            <p className="vet-chat-subtitle">
              {isTyping ? "Escribiendo…" : "Asistente veterinaria digital de Petfam"}
            </p>
          </div>

          <button className="close-btn" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>

        <div className="vet-chat-body">
          {renderContent()}

          {isTyping && (
            <div className="chat-row vet">
              <div className="vet-msg vet-typing">
                <span className="typing-dot" />
                <span className="typing-dot" />
                <span className="typing-dot" />
                <span className="typing-text">Dra.Vetia está escribiendo...</span>
              </div>
            </div>
          )}
        </div>

        <div className="vet-chat-footer">
          <p>
            Esta guía no reemplaza una consulta veterinaria presencial. Úsala como apoyo para organizar vacunas y desparasitaciones. 💛
          </p>
        </div>
      </div>
    </div>
  );
}